import { useCallback, useRef } from 'react';

/** Balayage gauche / droite avec animation (le contenu suit le doigt puis glisse) ; le défilement vertical reste natif. */
export const useSwipe = (onSwipeLeft, onSwipeRight, options = {}) => {
  const { threshold = 80, enabled = true } = options;
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const isHorizontalSwipe = useRef(null);
  const animatingRef = useRef(false);
  const targetRef = useRef(null);

  const setTransform = useCallback((offset, animate) => {
    const el = targetRef.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 0.2s ease-out' : 'none';
    el.style.transform = offset !== 0 ? `translateX(${offset}px)` : '';
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (!enabled || animatingRef.current) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = null;
  }, [enabled]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || touchStartX.current === null || animatingRef.current) return;
    const deltaX = e.touches[0].clientX - touchStartX.current;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (isHorizontalSwipe.current === null && (Math.abs(deltaX) > 25 || Math.abs(deltaY) > 25)) {
      isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY) * 2;
    }
    // Geste vertical : défilement natif ; geste horizontal : le contenu suit le doigt
    if (isHorizontalSwipe.current === true) setTransform(deltaX * 0.6, false);
  }, [enabled, setTransform]);

  const handleTouchEnd = useCallback((e) => {
    if (!enabled || touchStartX.current === null || animatingRef.current) return;
    if (isHorizontalSwipe.current !== true) {
      touchStartX.current = null;
      touchStartY.current = null;
      isHorizontalSwipe.current = null;
      return;
    }
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    animatingRef.current = true;
    if (Math.abs(deltaX) > threshold) {
      const swipedRight = deltaX > 0;
      const screenWidth = window.innerWidth;
      setTransform(swipedRight ? screenWidth : -screenWidth, true);
      setTimeout(() => {
        setTransform(swipedRight ? -screenWidth : screenWidth, false);
        if (swipedRight && onSwipeRight) onSwipeRight();
        else if (!swipedRight && onSwipeLeft) onSwipeLeft();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTransform(0, true);
            setTimeout(() => { animatingRef.current = false; }, 200);
          });
        });
      }, 180);
    } else {
      setTransform(0, true);
      setTimeout(() => { animatingRef.current = false; }, 180);
    }
    touchStartX.current = null;
    touchStartY.current = null;
    isHorizontalSwipe.current = null;
  }, [enabled, threshold, onSwipeLeft, onSwipeRight, setTransform]);

  return { ref: targetRef, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd };
};
